<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { eventsApi, playersApi } from '@/services/api';
import type { Event, EventType, PlayerProfile } from '@/types/models';
import AppBadge from '@/components/ui/AppBadge.vue';
import AppModal from '@/components/ui/AppModal.vue';
import EventAttendanceModal from '@/components/events/EventAttendanceModal.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NInput, NButton, NSelect, NTag } from 'naive-ui';
import {
  Calendar,
  Clock,
  MapPin,
  Plus,
  Trophy,
  CheckCircle2,
  Users,
  RefreshCw
} from 'lucide-vue-next';

const { t, locale } = useI18n();
const { activeTenant, managedTeams, currentUser } = useTenant();
const { notifySuccess, notifyError } = useToastNotification();

const isCoach = computed(() =>
  currentUser.value.role === 'ADMIN' ||
  currentUser.value.role === 'COACH' ||
  currentUser.value.role === 'HEAD_COACH' ||
  currentUser.value.role === 'ORG_ADMIN'
);

const events = ref<Event[]>([]);
const tenantPlayers = ref<PlayerProfile[]>([]);
const filterType = ref<string>('ALL');
const isCreateEventOpen = ref(false);
const isAttendanceOpen = ref(false);
const selectedEvent = ref<Event | null>(null);
const isLoading = ref(false);

const loadEventsAndPlayers = async () => {
  isLoading.value = true;
  try {
    const [fetchedEvents, fetchedPlayers] = await Promise.all([
      eventsApi.getEvents(undefined, activeTenant.value.id),
      playersApi.getPlayers(undefined, activeTenant.value.id),
    ]);
    events.value = fetchedEvents || [];
    tenantPlayers.value = fetchedPlayers || [];
  } catch (err) {
    console.warn('[EventsView] Load error:', err);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadEventsAndPlayers();
});

watch(() => activeTenant.value.id, () => {
  loadEventsAndPlayers();
});

const filteredEvents = computed(() => {
  return events.value
    .filter(e => {
      if (filterType.value !== 'ALL' && e.eventType !== filterType.value) return false;
      return true;
    })
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
});

const formatTime = (iso: string) => {
  return new Date(iso).toLocaleDateString(locale.value === 'ja' ? 'ja-JP' : 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const openAttendance = (evt: Event) => {
  selectedEvent.value = evt;
  isAttendanceOpen.value = true;
};

const newEventForm = ref({
  title: '',
  eventType: 'GAME' as EventType,
  startTime: '',
  endTime: '',
  location: '',
  opponent: '',
  homeAway: 'HOME' as 'HOME' | 'AWAY',
  notes: '',
});

const isSubmitting = ref(false);

const handleCreateEvent = async () => {
  if (!newEventForm.value.title || !newEventForm.value.location) return;

  isSubmitting.value = true;
  try {
    const created = await eventsApi.createEvent({
      tenantId: activeTenant.value.id,
      teamId: managedTeams.value[0]?.id || 'team_tokyo_gas_top',
      title: newEventForm.value.title,
      eventType: newEventForm.value.eventType,
      startTime: newEventForm.value.startTime || new Date().toISOString(),
      endTime: newEventForm.value.endTime || new Date(Date.now() + 7200000).toISOString(),
      location: newEventForm.value.location,
      opponent: newEventForm.value.opponent || undefined,
      homeAway: newEventForm.value.homeAway,
      notes: newEventForm.value.notes || undefined,
    });

    events.value.push(created);
    isCreateEventOpen.value = false;
    notifySuccess(t('events.eventScheduled'), t('events.eventScheduledMsg', { title: created.title }));

    // Reset form
    newEventForm.value = {
      title: '',
      eventType: 'GAME',
      startTime: '',
      endTime: '',
      location: '',
      opponent: '',
      homeAway: 'HOME',
      notes: '',
    };
  } catch (err: any) {
    notifyError('Schedule Event Error', err.message);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
          {{ t('events.title') }}
        </h1>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ t('events.subtitle', { name: activeTenant.name }) }}
        </p>
      </div>

      <div class="flex items-center gap-2.5 self-start sm:self-auto">
        <NButton
          secondary
          @click="loadEventsAndPlayers"
          :loading="isLoading"
          class="font-semibold text-xs"
          title="Refresh Schedule"
        >
          <template #icon>
            <RefreshCw class="w-4 h-4 text-sky-400" />
          </template>
          Sync
        </NButton>

        <NButton
          v-if="isCoach"
          type="primary"
          @click="isCreateEventOpen = true"
          class="font-bold text-xs shadow-lg shadow-sky-500/20"
        >
          <template #icon>
            <Plus class="w-4 h-4" />
          </template>
          {{ t('events.scheduleEvent') }}
        </NButton>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="flex items-center gap-2">
      <button
        type="button"
        @click="filterType = 'ALL'"
        :class="[
          'px-3 py-1.5 rounded-xl text-xs font-semibold transition',
          filterType === 'ALL' ? 'bg-sky-500/20 text-sky-300 border border-sky-500/30' : 'bg-slate-900 text-slate-400 hover:text-slate-200'
        ]"
      >
        {{ t('events.allEvents') }}
      </button>
      <button
        type="button"
        @click="filterType = 'GAME'"
        :class="[
          'px-3 py-1.5 rounded-xl text-xs font-semibold transition',
          filterType === 'GAME' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-900 text-slate-400 hover:text-slate-200'
        ]"
      >
        {{ t('events.gamesOnly') }}
      </button>
      <button
        type="button"
        @click="filterType = 'PRACTICE'"
        :class="[
          'px-3 py-1.5 rounded-xl text-xs font-semibold transition',
          filterType === 'PRACTICE' ? 'bg-sky-500/20 text-sky-300 border border-sky-500/30' : 'bg-slate-900 text-slate-400 hover:text-slate-200'
        ]"
      >
        {{ t('events.practices') }}
      </button>
    </div>

    <!-- Events List -->
    <div class="space-y-4">
      <div
        v-for="event in filteredEvents"
        :key="event.id"
        class="bg-slate-900 border border-slate-800 rounded-2xl p-5 hover:border-slate-700 transition"
      >
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div class="flex items-start gap-4">
            <div
              :class="[
                'w-14 h-14 rounded-2xl flex flex-col items-center justify-center font-extrabold text-xs shrink-0',
                event.eventType === 'GAME' ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400' : 'bg-sky-500/10 border border-sky-500/20 text-sky-400'
              ]"
            >
              <span>{{ event.eventType === 'GAME' ? t('coachDashboard.game') : t('coachDashboard.practice') }}</span>
            </div>

            <div>
              <div class="flex items-center gap-2">
                <h3 class="font-bold text-slate-100 text-base">{{ event.title }}</h3>
                <AppBadge v-if="event.homeAway" :variant="event.homeAway === 'HOME' ? 'success' : 'slate'">
                  {{ event.homeAway === 'HOME' ? t('common.home') : t('common.away') }}
                </AppBadge>
              </div>

              <div class="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                <span class="flex items-center gap-1.5 text-slate-300">
                  <Clock class="w-3.5 h-3.5 text-slate-400" />
                  {{ formatTime(event.startTime) }}
                </span>
                <span class="flex items-center gap-1.5">
                  <MapPin class="w-3.5 h-3.5 text-slate-500" />
                  {{ event.location }}
                </span>
              </div>

              <p v-if="event.opponent" class="mt-1 text-xs text-slate-400">
                VS: <strong class="text-slate-200">{{ event.opponent }}</strong>
              </p>
              <p v-if="event.notes" class="mt-1 text-xs text-slate-500 italic">
                {{ event.notes }}
              </p>
            </div>
          </div>

          <!-- Attendance badge metrics & Check-In Trigger -->
          <div class="flex md:flex-col items-end gap-2 border-t md:border-t-0 border-slate-800 pt-3 md:pt-0">
            <NButton
              size="small"
              secondary
              @click="openAttendance(event)"
              class="font-semibold text-xs"
            >
              <template #icon><Users class="w-3.5 h-3.5 text-sky-400" /></template>
              {{ t('events.rollCall') }}
            </NButton>
            <div class="text-[11px] text-slate-400 flex items-center gap-1">
              <CheckCircle2 class="w-3.5 h-3.5 text-emerald-400" />
              <span>{{ event.attendanceSummary?.attending || 14 }} {{ t('events.confirmed') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Create Event -->
    <AppModal
      v-model="isCreateEventOpen"
      :title="t('events.scheduleEvent')"
      :subtitle="t('events.subtitle', { name: activeTenant.name })"
    >
      <form @submit.prevent="handleCreateEvent" class="space-y-4">
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('events.eventTitle') }}</label>
          <NInput
            v-model:value="newEventForm.title"
            placeholder="e.g. League Doubleheader vs Tigers"
            required
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('events.eventType') }}</label>
            <select
              v-model="newEventForm.eventType"
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
            >
              <option value="GAME">Game</option>
              <option value="PRACTICE">Practice</option>
              <option value="SCRIMMAGE">Scrimmage</option>
              <option value="TRYOUT">Tryout</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('events.homeAway') }}</label>
            <select
              v-model="newEventForm.homeAway"
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
            >
              <option value="HOME">Home</option>
              <option value="AWAY">Away</option>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('events.opponent') }}</label>
            <NInput
              v-model:value="newEventForm.opponent"
              placeholder="e.g. Red Sox Youth"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('events.location') }}</label>
            <NInput
              v-model:value="newEventForm.location"
              placeholder="e.g. Field 3, Central Park"
              required
            />
          </div>
        </div>

        <div class="pt-4 flex justify-end gap-3 border-t border-slate-800">
          <NButton
            quaternary
            @click="isCreateEventOpen = false"
          >
            {{ t('teams.cancel') }}
          </NButton>
          <NButton
            type="primary"
            attr-type="submit"
            :loading="isSubmitting"
            class="font-bold text-xs"
          >
            {{ t('events.scheduleEvent') }}
          </NButton>
        </div>
      </form>
    </AppModal>

    <!-- Attendance Roll-Call Modal -->
    <EventAttendanceModal
      v-model="isAttendanceOpen"
      :event="selectedEvent"
      :players="tenantPlayers"
    />
  </div>
</template>
